import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProviderOptionsService } from "../../api/services/providerOptionsService";
import { ProviderOption } from "../../data-model/providerOptionModel";
import { DataState } from "../store/store";

interface ProviderOptionsConfigState {
  providerOptions: DataState<ProviderOption[]>;
}

const initialState: ProviderOptionsConfigState = {
  providerOptions: {
    loading: false,
    error: null,
    data: null,
  },
};

export const fetchProviderOptions = createAsyncThunk(
  "providerOptionsConfig/fetchProviderOptions",
  async (facilityId: string, { rejectWithValue }) => {
    try {
      return await ProviderOptionsService.getAllProviderOptions(facilityId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch provider options",
      );
    }
  },
);

export const createProviderOption = createAsyncThunk(
  "providerOptionsConfig/createProviderOption",
  async (
    {
      facilityId,
      data,
    }: { facilityId: string; data: Omit<ProviderOption, "id"> },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await ProviderOptionsService.createProviderOption(data, facilityId);
      dispatch(fetchProviderOptions(facilityId));
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to create provider option",
      );
    }
  },
);

export const updateProviderOption = createAsyncThunk(
  "providerOptionsConfig/updateProviderOption",
  async (
    {
      facilityId,
      docId,
      data,
    }: { facilityId: string; docId: string; data: Partial<ProviderOption> },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await ProviderOptionsService.updateProviderOption(
        docId,
        data,
        facilityId,
      );
      dispatch(fetchProviderOptions(facilityId));
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to update provider option",
      );
    }
  },
);

export const deleteProviderOption = createAsyncThunk(
  "providerOptionsConfig/deleteProviderOption",
  async (
    { facilityId, docId }: { facilityId: string; docId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await ProviderOptionsService.deleteProviderOption(docId, facilityId);
      dispatch(fetchProviderOptions(facilityId));
      return docId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to delete provider option",
      );
    }
  },
);

const providerOptionsConfigSlice = createSlice({
  name: "providerOptionsConfig",
  initialState,
  reducers: {
    clearProviderOptions: (state) => {
      state.providerOptions.data = null;
      state.providerOptions.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchProviderOptions.pending, (state) => {
        state.providerOptions.loading = true;
        state.providerOptions.error = null;
      })
      .addCase(
        fetchProviderOptions.fulfilled,
        (state, action: PayloadAction<ProviderOption[]>) => {
          state.providerOptions.loading = false;
          state.providerOptions.data = action.payload;
        },
      )
      .addCase(fetchProviderOptions.rejected, (state, action) => {
        state.providerOptions.loading = false;
        state.providerOptions.error = action.payload as string;
      })

      // Create
      .addCase(createProviderOption.pending, (state) => {
        state.providerOptions.loading = true;
        state.providerOptions.error = null;
      })
      .addCase(createProviderOption.fulfilled, (state) => {
        state.providerOptions.loading = false;
      })
      .addCase(createProviderOption.rejected, (state, action) => {
        state.providerOptions.loading = false;
        state.providerOptions.error = action.payload as string;
      })

      // Update
      .addCase(updateProviderOption.pending, (state) => {
        state.providerOptions.loading = true;
        state.providerOptions.error = null;
      })
      .addCase(updateProviderOption.fulfilled, (state) => {
        state.providerOptions.loading = false;
      })
      .addCase(updateProviderOption.rejected, (state, action) => {
        state.providerOptions.loading = false;
        state.providerOptions.error = action.payload as string;
      })

      // Delete
      .addCase(deleteProviderOption.pending, (state) => {
        state.providerOptions.loading = true;
        state.providerOptions.error = null;
      })
      .addCase(deleteProviderOption.fulfilled, (state) => {
        state.providerOptions.loading = false;
      })
      .addCase(deleteProviderOption.rejected, (state, action) => {
        state.providerOptions.loading = false;
        state.providerOptions.error = action.payload as string;
      });
  },
});

export const { clearProviderOptions } = providerOptionsConfigSlice.actions;
export default providerOptionsConfigSlice.reducer;
